import { useEffect, useState } from "react";
import { Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { FiltersEditor, RecipientCount } from "./SegmentBuilder";
import ChannelContentEditor from "./ChannelContentEditor";

const CHANNELS = [
  { key: "email", label: "Email", desc: "Newsletter e promozioni" },
  { key: "whatsapp", label: "WhatsApp", desc: "Messaggi diretti (provider dedicato)" },
  { key: "sms", label: "SMS", desc: "Testo breve, alta lettura" },
  { key: "whatsapp_api", label: "WhatsApp API", desc: "Template Meta approvati" },
];

const MODES = [
  { key: "immediato", label: "Invio immediato", desc: "Parte subito" },
  { key: "programmata", label: "Programmata", desc: "Data e ora precise" },
  { key: "graduale", label: "Graduale", desc: "Distribuita in più ore" },
  { key: "predittiva", label: "Predittiva", desc: "Orario ottimale (presto disponibile)" },
];

const STEPS = ["Informazioni", "Destinatari", "Contenuto", "Invio"];

export default function CampaignWizard({ campaign, onClose, onSaved }) {
  const [step, setStep] = useState(0);
  const [campId, setCampId] = useState(campaign?.id || null);
  const [name, setName] = useState(campaign?.name || "");
  const [channel, setChannel] = useState(campaign?.channel || "email");
  const [recipientMode, setRecipientMode] = useState(
    campaign?.segment_id ? "segmento" : campaign?.segment?.filters?.length ? "nuovo" : "tutti");
  const [segmentId, setSegmentId] = useState(campaign?.segment_id || "");
  const [segments, setSegments] = useState([]);
  const [combinator, setCombinator] = useState(campaign?.segment?.combinator || "and");
  const [filters, setFilters] = useState(campaign?.segment?.filters || []);
  const [saveSegmentName, setSaveSegmentName] = useState("");
  const [content, setContent] = useState(campaign?.content || {});
  const [sendMode, setSendMode] = useState(campaign?.send_mode || "immediato");
  const [scheduledAt, setScheduledAt] = useState(campaign?.scheduled_at || "");
  const [gradualHours, setGradualHours] = useState(campaign?.gradual_hours || 4);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [services, setServices] = useState(null);
  const [includeNoConsent, setIncludeNoConsent] = useState(campaign?.include_no_consent || false);

  useEffect(() => {
    api.get("/marketing/segments").then((r) => setSegments(r.data)).catch(() => {});
    api.get("/integrations/services").then((r) => setServices(r.data.services)).catch(() => {});
  }, []);

  const channelActive = services ? services.find((s) => s.key === channel)?.attivo : null;

  useEffect(() => {
    const t = setTimeout(() => {
      const seg = recipientMode === "segmento" ? segments.find((s) => s.id === segmentId) : null;
      api.post("/marketing/segments/preview", {
        combinator: seg ? (seg.combinator || "and") : combinator,
        filters: recipientMode === "tutti" ? [] : seg ? (seg.filters || []) : filters,
        channel,
        chars: (content.body || "").length,
        include_no_consent: includeNoConsent,
      }).then((r) => setPreview(r.data)).catch(() => setPreview({ error: true }));
    }, 400);
    return () => clearTimeout(t);
  }, [combinator, filters, channel, recipientMode, content.body, segmentId, segments, includeNoConsent]);

  const segmentPayload = () => {
    if (recipientMode === "tutti") return { segment_id: null, segment: { all: true } };
    if (recipientMode === "segmento") return { segment_id: segmentId || null, segment: null };
    return { segment_id: null, segment: { combinator, filters } };
  };

  const saveDraft = async () => {
    const payload = { name, channel, content, send_mode: sendMode, include_no_consent: includeNoConsent,
      scheduled_at: scheduledAt || null, gradual_hours: parseInt(gradualHours) || 4, ...segmentPayload() };
    if (campId) {
      const r = await api.patch(`/marketing/campaigns/${campId}`, payload);
      return r.data.id;
    }
    const r = await api.post("/marketing/campaigns", payload);
    setCampId(r.data.id);
    return r.data.id;
  };

  const next = async () => {
    if (step === 0 && !name.trim()) { toast.error("Inserisci il nome della campagna"); return; }
    if (step === 1 && recipientMode === "segmento" && !segmentId) { toast.error("Seleziona un segmento"); return; }
    try {
      await saveDraft();
      setStep(step + 1);
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveNewSegment = async () => {
    if (!saveSegmentName.trim() || filters.length === 0) return;
    try {
      const r = await api.post("/marketing/segments", { name: saveSegmentName, combinator, filters });
      setSegments((s) => [...s, r.data]);
      setRecipientMode("segmento");
      setSegmentId(r.data.id);
      toast.success("Segmento salvato");
    } catch (e) { toast.error(apiError(e)); }
  };

  const confirmSend = async () => {
    setSending(true);
    try {
      const id = await saveDraft();
      const r = await api.post(`/marketing/campaigns/${id}/send`, {
        send_mode: sendMode, scheduled_at: scheduledAt || null, gradual_hours: parseInt(gradualHours) || 4,
      });
      toast.success(sendMode === "programmata"
        ? `Campagna programmata per ${r.data.recipients} destinatari`
        : r.data.mock
          ? `Invio avviato (MOCK) a ${r.data.recipients} destinatari`
          : `Invio avviato a ${r.data.recipients} destinatari — l'avanzamento si aggiorna da solo nella lista`);
      onSaved();
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  const inputCls = "w-full h-11 px-3 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900";
  const labelCls = "block text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-1";
  const channelLabel = CHANNELS.find((c) => c.key === channel)?.label;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-xl rounded-t-xl max-h-[94vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="campaign-wizard">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
          <h2 className="font-display font-bold text-lg">{campaign ? "Modifica campagna" : "Nuova campagna"}</h2>
          <button onClick={onClose} className="p-2 text-zinc-500" data-testid="wizard-close"><X size={18} /></button>
        </div>

        <div className="flex px-5 pt-4 gap-1">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`h-1 rounded-full ${i <= step ? "bg-zinc-900" : "bg-zinc-200"}`} />
              <div className={`text-[10px] mt-1 font-semibold ${i === step ? "text-zinc-900" : "text-zinc-400"}`}>{i + 1}. {s}</div>
            </div>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {step === 0 && (
            <>
              <div><label className={labelCls}>Nome campagna *</label>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Es. Promo Weekend di Ferragosto"
                  className={inputCls} data-testid="wizard-name" /></div>
              <div>
                <label className={labelCls}>Canale</label>
                <div className="grid grid-cols-2 gap-2">
                  {CHANNELS.map((c) => (
                    <button type="button" key={c.key} onClick={() => setChannel(c.key)} data-testid={`wizard-channel-${c.key}`}
                      className={`text-left border-2 rounded-lg p-3 transition ${channel === c.key ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"}`}>
                      <div className="text-sm font-bold text-zinc-900 flex items-center gap-1.5">
                        {c.label} {channel === c.key && <Check size={14} weight="bold" />}
                      </div>
                      <div className="text-[11px] text-zinc-500">{c.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <div className="flex gap-1.5 flex-wrap">
                {[["tutti", "Tutti i clienti"], ["segmento", "Segmento esistente"], ["nuovo", "Nuovo segmento"]].map(([k, l]) => (
                  <button type="button" key={k} onClick={() => setRecipientMode(k)} data-testid={`recipient-${k}`}
                    className={`h-9 px-3 rounded-full text-xs font-semibold border ${recipientMode === k ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200"}`}>
                    {l}
                  </button>
                ))}
              </div>
              {recipientMode === "segmento" && (
                <select value={segmentId} onChange={(e) => setSegmentId(e.target.value)} className={inputCls} data-testid="segment-select">
                  <option value="">Seleziona segmento...</option>
                  {segments.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.filters.length} filtri)</option>)}
                </select>
              )}
              {recipientMode === "nuovo" && (
                <>
                  <FiltersEditor combinator={combinator} filters={filters}
                    onChange={({ combinator: c, filters: f }) => { setCombinator(c); setFilters(f); }} />
                  {filters.length > 0 && (
                    <div className="flex gap-2">
                      <input value={saveSegmentName} onChange={(e) => setSaveSegmentName(e.target.value)}
                        placeholder="Salva come segmento: nome..." className={inputCls} data-testid="segment-name" />
                      <button type="button" onClick={saveNewSegment} data-testid="segment-save"
                        className="h-11 px-4 rounded-md bg-zinc-100 text-xs font-bold text-zinc-700 shrink-0">Salva</button>
                    </div>
                  )}
                </>
              )}
              <RecipientCount preview={preview} />
              <div className="space-y-1.5" data-testid="consent-mode">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Destinatari per consenso</p>
                <div className="flex gap-1.5 flex-wrap">
                  {[[false, "Solo consenso marketing"], [true, "Tutti, anche senza consenso"]].map(([v, l]) => (
                    <button type="button" key={String(v)} onClick={() => setIncludeNoConsent(v)}
                      data-testid={v ? "consent-all" : "consent-only"}
                      className={`h-9 px-3 rounded-full text-xs font-semibold border transition ${
                        includeNoConsent === v ? "bg-zinc-900 text-white border-zinc-900"
                          : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`}>
                      {l}
                    </button>
                  ))}
                </div>
                {includeNoConsent ? (
                  <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">
                    Attenzione: invierai anche a contatti <b>senza consenso marketing</b>. Assicurati di avere una
                    base giuridica valida (es. soft opt-in per clienti esistenti su prodotti simili): la
                    responsabilità GDPR resta del titolare.
                  </p>
                ) : (
                  <p className="text-[11px] text-zinc-400">I contatti senza consenso marketing per questo canale vengono esclusi automaticamente.</p>
                )}
              </div>
            </>
          )}

          {step === 2 && (
            <ChannelContentEditor channel={channel} content={content} onChange={setContent}
              campaignId={campId} recipients={preview?.eligible || 0} />
          )}

          {step === 3 && (
            <>
              <div>
                <label className={labelCls}>Modalità di invio</label>
                <div className="grid grid-cols-2 gap-2">
                  {MODES.map((m) => (
                    <button type="button" key={m.key} onClick={() => setSendMode(m.key)} data-testid={`mode-${m.key}`}
                      className={`text-left border-2 rounded-lg p-3 transition ${sendMode === m.key ? "border-zinc-900 bg-zinc-50" : "border-zinc-200"}`}>
                      <div className="text-sm font-bold text-zinc-900">{m.label}</div>
                      <div className="text-[11px] text-zinc-500">{m.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
              {sendMode === "programmata" && (
                <div><label className={labelCls}>Data e ora di invio</label>
                  <input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)}
                    className={inputCls} data-testid="scheduled-at" /></div>
              )}
              {sendMode === "graduale" && (
                <div><label className={labelCls}>Distribuisci in (ore)</label>
                  <input type="number" min="1" max="48" value={gradualHours} onChange={(e) => setGradualHours(e.target.value)}
                    className={inputCls} data-testid="gradual-hours" /></div>
              )}

              <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4 space-y-1.5" data-testid="send-summary">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-2">Conferma invio</div>
                <div className="text-sm text-zinc-700"><span className="text-zinc-500">Campagna:</span> <strong>{name}</strong></div>
                <div className="text-sm text-zinc-700"><span className="text-zinc-500">Canale:</span> {channelLabel}</div>
                <div className="text-sm text-zinc-700"><span className="text-zinc-500">Segmento:</span> {recipientMode === "tutti" ? "Tutti i clienti" : recipientMode === "segmento" ? segments.find((s) => s.id === segmentId)?.name : "Nuovo segmento"}</div>
                <div className="text-sm text-zinc-700"><span className="text-zinc-500">Destinatari (con consenso):</span> <strong>{preview?.eligible ?? "—"}</strong></div>
                <div className="text-sm text-zinc-700"><span className="text-zinc-500">Costo stimato:</span> <strong>€{preview?.estimated_cost ?? "—"}</strong></div>
                <div className="text-sm text-zinc-700"><span className="text-zinc-500">Invio:</span> {MODES.find((m) => m.key === sendMode)?.label}{sendMode === "programmata" && scheduledAt ? ` — ${scheduledAt.replace("T", " ")}` : ""}</div>
                {channelActive ? (
                  <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mt-2" data-testid="send-real-banner">
                    Invio REALE attivo: i messaggi verranno spediti davvero ai destinatari.
                  </div>
                ) : (
                  <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2" data-testid="send-mock-banner">
                    Invii MOCK: nessun messaggio reale verrà spedito fino all'integrazione di un provider.
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="flex gap-2 px-5 py-4 border-t border-zinc-200">
          {step > 0 && (
            <button onClick={() => setStep(step - 1)} data-testid="wizard-back"
              className="h-11 px-4 rounded-md border border-zinc-300 text-sm font-semibold text-zinc-700">Indietro</button>
          )}
          <div className="flex-1" />
          {step < 3 ? (
            <button onClick={next} data-testid="wizard-next"
              className="h-11 px-6 rounded-md bg-zinc-900 text-white text-sm font-semibold">Avanti</button>
          ) : (
            <button onClick={confirmSend} disabled={sending} data-testid="wizard-confirm-send"
              className="h-11 px-6 rounded-md bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 active:scale-[0.98] transition disabled:opacity-50">
              {sending ? "Invio..." : sendMode === "programmata" ? "Programma campagna" : "Conferma e invia"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
